import store from "@/store";
import router from "@/router";
import i18n from "@/i18n";
import Notify from 'quasar/src/plugins/Notify.js';;
import { OrderStatusEnum } from "@/types";
import { isEmpty } from "@/utils/configuration/formatters-config";

/**
 * Demarrage du workflow d'un dossier : qui y a droit, quand, et ce que le clic declenche.
 *
 * Ces regles vivaient dans `OrderButtons`, seul endroit qui portait le bouton. Le bandeau
 * de synthese propose desormais la meme action sur sa puce « action suivante » : les
 * recopier aurait fait deux verites, dont l'une aurait fini par diverger de l'autre — le
 * bouton reste visible quand la puce ne l'est plus, ou l'inverse. `OrderButtons` s'y
 * adosse comme la puce ; aucun des deux ecrans ne decide plus par lui-meme.
 *
 * Tout se lit dans le store : aucune de ces regles n'a besoin d'un etat de composant.
 */

/** Statuts sur lesquels le dossier est clos : le workflow n'y est plus propose. */
const STATUTS_CLOS = [
  OrderStatusEnum.VALIDATED_CLIENT,
  OrderStatusEnum.ARCHIVING,
  OrderStatusEnum.CANCELLED,
];

/**
 * Champs obligatoires manquants sur un client professionnel.
 *
 * Le dossier ne peut ni etre enregistre ni voir son workflow demarrer tant qu'ils ne sont
 * pas renseignes : la liste sert a le dire a l'utilisateur, champ par champ.
 */
export function professionalClientMissingFields(): string[] {
  const missingFields = new Set<string>();

  store.state.orderPartyModule.orderParties.forEach((orderParty: any) => {
    if (orderParty.associatedParty.third.type.id === "party-FrenchProfessional") {
      const person = orderParty.associatedParty.third.person;
      const legalEntity = orderParty.associatedParty.third.person.legalEntity;

      if (!person || isEmpty(person.familyName)) {
        missingFields.add("Nom");
      }
      if (!person || isEmpty(person.firstName)) {
        missingFields.add("Prénom");
      }
      if (!person || isEmpty(person.title?.type?.value)) {
        missingFields.add("Titre");
      }

      if (!legalEntity || isEmpty(legalEntity.siren)) {
        missingFields.add("SIREN");
      }
      if (!legalEntity || isEmpty(legalEntity.legalCategory?.type?.value)) {
        missingFields.add("Catégorie juridique");
      }
      if (!legalEntity || isEmpty(legalEntity.creationDate)) {
        missingFields.add("Date de création");
      }
    }
  });

  return Array.from(missingFields);
}

/** Signale les champs manquants et repond vrai si l'action doit etre interrompue. */
export function notifyProfessionalClientMissingFields(): boolean {
  const missingFields = professionalClientMissingFields();

  if (!missingFields.length) {
    return false;
  }

  Notify.create({
    message: `Veuillez compléter les informations obligatoires du client professionnel (${missingFields.join(", ")}) avant d'enregistrer la commande.`,
    color: "negative",
  });

  return true;
}

/** Utilisateur en consultation seule : auditeur, mode VIEW, ou enregistrement desactive. */
export function isOrderViewMode(): boolean {
  const user = store.getters["authModule/getUserConnected"];
  const isAudit = user?.authorities?.find(
    (el: { authority: string }) => el.authority === "ROLE_AUDITORS"
  );
  return store.state.orderModule?.mode === "VIEW" || !!isAudit || store.state.orderModule.disableSave;
}

/**
 * Dossier dont le workflow n'a pas encore demarre (statut initial ORDER).
 *
 * Les tiroirs du dossier ne sont crees qu'au demarrage
 */
export function isOrderNotStarted(order: any): boolean {
  return !!order?.resourceUid && (order?.status?.resourceUid || OrderStatusEnum.ORDER) === OrderStatusEnum.ORDER;
}

/** Roles de l'utilisateur connecte, a plat. */
function rolesUtilisateur(): string[] {
  return store.getters["authModule/getUserConnected"]?.authorities?.map((r: any) => r.authority) || [];
}

/**
 * `disableWF` d'origine : hors BODEMER le workflow est toujours propose ; sur BODEMER, un
 * commercial sans autre casquette ne le demarre que sur un statut encore editable.
 */
export function isWorkflowStartEnabled(order: any): boolean {
  const configuration = store.state?.orderModule?.order?.orderNetworks?.network?.resourceUid?.id;
  if (configuration != "BODEMER") {
    return true;
  }
  const roles = rolesUtilisateur();
  const orderStatus = order?.status?.resourceUid || 'ORDER';

  const isSales = roles.includes("ROLE_SALES");
  const hasOtherRole = ["ROLE_SECRETARY", "ROLE_ASS_COMM", "ROLE_LIV", "ROLE_ASSISTANT", "ROLE_AGENT", "ROLE_CDV", "ROLE_PREPATEUR"]
    .some(role => roles.includes(role));

  const editableStatuses = ["ORDER", "ORDER_STARTED", "ORDER_REJECTED", "ORDER_NOT_VALIDATED", "ORDER_FG_VALIDATED", "ORDER_POST_DELIVERED"];

  return !(isSales && !hasOtherRole && !editableStatuses.includes(orderStatus));
}

/**
 * `removeWF` d'origine : des qu'une tache existe sur le dossier le workflow tourne deja ;
 * sinon le droit de le demarrer est lu dans la configuration, statut par statut.
 */
export function isWorkflowStartVisible(order: any): boolean {
  const roles = rolesUtilisateur();
  const orderStatus = order?.status?.resourceUid || 'ORDER';

  if (store.state.taskModule.tasks?.length > 0) {
    return true;
  }

  const configurationWorkflow = store.state.authModule?.configuration?.workflow;
  const entree = configurationWorkflow?.find((item: any) => item && item.field === orderStatus);
  const allowedGroups = entree ? entree.fiedld_value : undefined;
  if (!allowedGroups) {
    return false;
  }
  const groups = typeof allowedGroups === 'string' ? JSON.parse(allowedGroups) : allowedGroups;

  return roles.some((r: any) => groups.includes(r));
}

/**
 * Condition d'affichage complete du declencheur de workflow, telle que la portait le
 * `v-if` du bouton : un dossier reference et enregistre, pas clos, un utilisateur qui
 * n'est pas auditeur, et les deux regles de droit ci-dessus.
 */
export function canStartOrderWorkflow(order: any): boolean {
  const user = store.getters["authModule/getUserConnected"];
  const isAudit = user?.authorities?.find(
    (el: { authority: string }) => el.authority === "ROLE_AUDITORS"
  );
  return !!order?.reference
    && !!order?.resourceUid
    && !STATUTS_CLOS.includes(order?.status?.resourceUid)
    && !isAudit
    && isWorkflowStartEnabled(order)
    && isWorkflowStartVisible(order);
}

/**
 * Demarre le workflow du dossier.
 *
 * `onDone` est rappele a la fin, que l'orchestrateur ait abouti ou non : c'est lui qui
 * eteint l'indicateur de chargement de l'appelant. Il l'est aussi quand les champs
 * obligatoires manquent, sans quoi le declencheur resterait bloque en chargement.
 */
export function startOrderWorkflow(onDone?: () => void, onMultipleTasks?: (tasks: any[]) => void): void {
  if (notifyProfessionalClientMissingFields()) {
    if (onDone) {
      onDone();
    }
    return;
  }
  store.dispatch("orderModule/initOrderWorkflow", {
    callback: () => {
      if (onDone) {
        onDone();
      }
    },
    onMultipleTasks,
  });
}

/**
 * Libelle d'une tache a partir de sa `taskDefinition`.
 *
 * A defaut de traduction, le code est rendu lisible plutot que d'afficher un chemin
 * de cle : `task.actions` ne couvre pas toutes les `taskDefinition`.
 */
export function taskActionLabel(definition?: string): string {
  if (!definition) {
    return "";
  }
  const cle = `task.actions.${definition}`;
  if (i18n.global.te(cle)) {
    return i18n.global.t(cle) as string;
  }
  return String(definition).replace(/^STEP_/, "").replace(/[_-]+/g, " ").toLowerCase();
}

/** Ouvre une tache de dossier deja prise en charge, comme le fait `claimOwnership`. */
export function openOrderTask(task: any): void {
  task.isLoading = true;
  store.dispatch("taskModule/getSelectedTaskOrderDetail", {
    task,
    callback: () => {
      router.push("/task/detail").then(() => (task.isLoading = false));
    },
  });
}
